import { useMemo } from 'react';

import settings from '~/settings';
import { CURRENCY, ATTRIBUTES_VALIDATION_STATE } from '~/constants';
import { t } from '~/helpers/localization';
import { ROLE_NAMES } from '~/roles';

import ErrorDublon from '~/UIKit/ErrorDublon/ErrorDublon';

import type { ICreateErrorsCommon } from '~/Actions/ActionClanCreate';
import type { IClanRenameStateFields } from '~/Reducers/ReducerClanRename';
import type { ICurrentAccountState } from '~/Reducers/ReducerCurrentAccount';
import type { IApiError } from '~/types/api';

interface ButtonStateParams {
  needGold: number;
  cost: number;
  tagChanged: boolean;
  nameChanged: boolean;
  cooldownMessage: Nullable<string>;
  tag: IClanRenameStateFields['tag'];
  name: IClanRenameStateFields['name'];
  inputsErrors: { tag: Nullable<string>; name: Nullable<string> };
  error: IApiError;
}

const computeRenameButtonState = ({
  needGold,
  cost,
  tagChanged,
  nameChanged,
  cooldownMessage,
  tag,
  name,
  inputsErrors,
  error,
}: ButtonStateParams): { disabled: boolean; tooltip: React.ReactNode } => {
  if (needGold > 0) {
    return {
      disabled: true,
      tooltip: <ErrorDublon amount={cost} deficit={needGold} />,
    };
  }

  if (!tagChanged && !nameChanged) {
    return {
      disabled: true,
      tooltip: t('Измените тег или название'),
    };
  }

  if (cooldownMessage) {
    return {
      disabled: true,
      tooltip: cooldownMessage,
    };
  }

  if (tag.error || name.error || inputsErrors.tag || inputsErrors.name) {
    return {
      disabled: true,
      tooltip: t('Введите корректные тег и название'),
    };
  }

  if (
    tag.available === ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE ||
    name.available === ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE
  ) {
    return {
      disabled: true,
      tooltip: t('Клан с таким тегом или названием уже зарегистрирован'),
    };
  }

  const hasApiMessage = (e: unknown): e is IApiError =>
    e !== null && typeof e === 'object' && !Array.isArray(e) && 'message' in (e as Record<string, unknown>);

  if (error && typeof error !== 'string' && hasApiMessage(error) && error.message) {
    return {
      disabled: true,
      tooltip: error.message,
    };
  }

  return {
    disabled: false,
    tooltip: null,
  };
};

export interface ClanNameEditDerivedState {
  values: { tag: string; name: string };
  errors: { tag: Nullable<string>; name: Nullable<string> };
  cost: number;
  needGold: number;
  button: { disabled: boolean; tooltip: React.ReactNode };
}

interface UseClanNameEditStateParams {
  clan: Nullable<{ tag: string; name: string }>;
  currentAccount: ICurrentAccountState;
  error: IApiError;
  cooldownMessage: Nullable<string>;
  name: IClanRenameStateFields['name'];
  tag: IClanRenameStateFields['tag'];
}

export const useClanNameEditState = ({
  clan,
  currentAccount,
  error,
  cooldownMessage,
  name,
  tag,
}: UseClanNameEditStateParams): ClanNameEditDerivedState =>
  useMemo(() => {
    const isCommanderFreeRename =
      currentAccount.isFreeRenameAvailable && currentAccount.roleName === ROLE_NAMES.COMMANDER;
    const cost = isCommanderFreeRename ? 0 : settings.clanRename.cost;

    const clanTag = clan?.tag ?? '';
    const clanName = clan?.name ?? '';
    const goldBalance = currentAccount.balance?.[CURRENCY.GOLD] ?? 0;

    const errorsObj = (error as ICreateErrorsCommon)?.errors ?? {};
    const inputsErrors = {
      tag: (errorsObj.tag ?? [])[0] ?? null,
      name: (errorsObj.name ?? [])[0] ?? null,
    };

    const tagValue = tag.value === null ? clanTag : tag.value;
    const nameValue = name.value === null ? clanName : name.value;

    const needGold = Math.max(cost - goldBalance, 0);

    const tagChanged = Boolean(tagValue && tagValue !== clanTag);
    const nameChanged = Boolean(nameValue && nameValue !== clanName);

    const button = computeRenameButtonState({
      needGold,
      cost,
      tagChanged,
      nameChanged,
      cooldownMessage,
      tag,
      name,
      inputsErrors,
      error,
    });

    return {
      values: {
        tag: tagValue,
        name: nameValue,
      },
      errors: inputsErrors,
      cost,
      needGold,
      button,
    };
  }, [
    currentAccount.isFreeRenameAvailable,
    currentAccount.roleName,
    currentAccount.balance,
    clan?.tag,
    clan?.name,
    error,
    tag,
    name,
    cooldownMessage,
  ]);
